import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AUDIENCE_LABEL, type PackActivation, type PackInfo, type RiskProfile } from "@ti/core";
import { ArrowRight, CheckCircle2, ChevronRight, Hourglass, Loader2, ShieldAlert, ShieldCheck, Users } from "lucide-react";
import { type ReactNode, useState } from "react";
import { TierBadge } from "@/components/domain/status";
import { QueryState } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, Field, Meter, Skeleton } from "@/components/ui/misc";
import { unconfirmedHighRisk, unconfirmedTools } from "@/features/profile/labels";
import { Callout, ReviewStep, type StepStatus, StepMarker } from "@/features/profile/review-step";
import { SuggestionCard } from "@/features/profile/suggestion-card";
import { ToolsTable } from "@/features/profile/tools-table";
import { TopologyGraph } from "@/features/profile/topology-graph";
import { q, useConfirmTool } from "@/lib/api";
import { fmtDate, fmtInt, fmtMoney, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/agents/$agentId/profile")({
  component: RiskProfileTab,
});

const PRINCIPAL_TARGET = 0.95;

function RiskProfileTab() {
  const { agentId } = Route.useParams();
  const query = useQuery(q.riskProfile(agentId));
  return (
    <QueryState query={query} loading={<ProfileSkeleton />}>
      {(profile) => <ProfileReview agentId={agentId} profile={profile} />}
    </QueryState>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading risk profile">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-72 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function useConfirmAll(agentId: string) {
  const confirm = useConfirmTool(agentId);
  const [running, setRunning] = useState(false);
  const run = async (tools: string[]) => {
    setRunning(true);
    try {
      for (const tool of tools) await confirm.mutateAsync({ tool });
    } catch {
      // per-tool errors are surfaced by the mutation's toast
    } finally {
      setRunning(false);
    }
  };
  return { run, running: running || confirm.isPending };
}

interface StepDef {
  id: string;
  title: string;
  status: StepStatus;
}

function ProfileReview({ agentId, profile }: { agentId: string; profile: RiskProfile }) {
  const pendingAll = unconfirmedTools(profile);
  const pendingHigh = unconfirmedHighRisk(profile);
  const uncontrolled = profile.authority.filter((r) => !r.controlled);
  const toAdopt = profile.suggestions;
  const confirmAll = useConfirmAll(agentId);
  const enabledPacks = profile.packs.filter((p) => p.enabled);
  const credible = profile.baseline.status === "credible";
  const tools = profile.topology.nodes.filter((n) => n.kind === "tool").length;
  const servers = profile.topology.nodes.filter((n) => n.kind === "mcp_server").length;
  const models = profile.topology.nodes.filter((n) => n.kind === "model").length;
  const subagents = profile.topology.nodes.filter((n) => n.kind === "subagent").length;
  const external = profile.audience === "external_user";
  const registry = useQuery(q.packs()).data;

  const steps: StepDef[] = [
    { id: "inventory", title: "Inventory", status: "done" },
    { id: "actions", title: "Actions", status: pendingAll.length ? "attention" : "done" },
    { id: "authority", title: "Authority", status: uncontrolled.length ? "attention" : "done" },
    external
      ? { id: "principals", title: "End users", status: profile.principals.resolvedShare >= PRINCIPAL_TARGET ? "done" : "attention" }
      : { id: "principals", title: "Internal users / systems", status: "done" },
    { id: "packs", title: "Detector packs", status: "done" },
    { id: "suggestions", title: "Suggested controls", status: toAdopt.length ? "attention" : "done" },
    { id: "baseline", title: "Baseline", status: credible ? "done" : "info" },
    { id: "projection", title: "Projection", status: "info" },
  ];
  const step = (id: string) => {
    const i = steps.findIndex((s) => s.id === id);
    return { id, n: i + 1, title: steps[i]!.title, status: steps[i]!.status };
  };
  const confirmButton = (label: string, size: "sm" | "md" = "sm") =>
    pendingAll.length ? (
      <Button size={size} disabled={confirmAll.running} onClick={() => void confirmAll.run(pendingAll.map((t) => t.tool))}>
        {confirmAll.running ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
        {label}
      </Button>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <StatusBanner profile={profile} pendingHigh={pendingHigh.length} pendingAll={pendingAll.length} action={confirmButton(`Confirm ${pendingAll.length === 1 ? "tool" : `all ${pendingAll.length}`}`)} />

      <Card className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 lg:grid-cols-5">
        <Field label="Audience">{AUDIENCE_LABEL[profile.audience]}</Field>
        <Field label="Active packs">
          {enabledPacks.map((p) => p.name).join(", ") || "None"}
        </Field>
        <Field label="Baseline">{credible && profile.baseline.credibleSince ? `Credible since ${fmtDate(profile.baseline.credibleSince)}` : `Learning · ${fmtPct(profile.baseline.sessions / profile.baseline.target)} of target`}</Field>
        <Field label="Limit after review">
          <span className="inline-flex items-center gap-1.5 tabular">
            {fmtMoney(profile.projection.currentLimit, { compact: true })}
            <TierBadge tier={profile.projection.currentTier} showLabel={false} />
            <ArrowRight className="size-3 text-fg-3" aria-label="to" />
            {fmtMoney(profile.projection.projectedLimit, { compact: true })}
            <TierBadge tier={profile.projection.projectedTier} showLabel={false} />
          </span>
        </Field>
        {profile.projection.earnedLimit ? (
          <Field label="Earned limit">
            <span className="tabular" title="The most new quotes can insure for this agent today. It grows with clean months, platform controls, human approvals and a better tier.">
              {fmtMoney(profile.projection.earnedLimit, { compact: true })}
            </span>
          </Field>
        ) : null}
      </Card>

      <nav aria-label="Review checklist" className="flex flex-wrap items-center gap-1 rounded-lg border bg-surface px-2 py-1.5">
        <span className="px-2 text-[11px] font-medium uppercase tracking-wide text-fg-3">
          Review · {steps.filter((s) => s.status === "done").length}/{steps.length}
        </span>
        {steps.map((s, i) => (
          <a key={s.id} href={`#${s.id}`} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-fg-2 hover:bg-surface-2 hover:text-fg">
            <StepMarker n={i + 1} status={s.status} size="sm" />
            {s.title}
          </a>
        ))}
      </nav>

      <ReviewStep
        {...step("inventory")}
        summary={[`${models} model${models === 1 ? "" : "s"}`, subagents ? `${subagents} sub-agents` : null, `${servers} MCP server${servers === 1 ? "" : "s"}`, `${tools} tool${tools === 1 ? "" : "s"}`].filter(Boolean).join(" · ") + " — discovered from traces"}
      >
        {profile.topology.nodes.length ? (
          <TopologyGraph nodes={profile.topology.nodes} edges={profile.topology.edges} unconfirmed={new Set(pendingAll.map((t) => t.tool))} />
        ) : (
          <EmptyState title="No topology yet" description="The inventory builds from the first traces: agents, models, MCP servers and tools." />
        )}
      </ReviewStep>

      <ReviewStep
        {...step("actions")}
        summary={
          pendingAll.length
            ? `${pendingAll.length} of ${profile.tools.length} tools await confirmation. Low-confidence classifications default to high risk until you confirm.`
            : `All ${profile.tools.length} tools confirmed. New tools are classified automatically and appear here for review.`
        }
        actions={confirmButton("Confirm all")}
        bodyClassName="p-0"
      >
        <ToolsTable agentId={agentId} tools={profile.tools} />
      </ReviewStep>

      <ReviewStep
        {...step("authority")}
        summary={`${profile.authority.length} rules extracted from the system prompt and policy docs · ${profile.authority.length - uncontrolled.length} controlled by the agent platform`}
        actions={
          <Link to="/agents/$agentId/authority" params={{ agentId }} className={buttonVariants({ variant: "outline", size: "sm" })}>
            Review authority <ChevronRight />
          </Link>
        }
      >
        {uncontrolled.length ? (
          <ul className="flex flex-col gap-2">
            {uncontrolled.map((r) => (
              <li key={r.id} className="flex flex-wrap items-start justify-between gap-2 text-[13px]">
                <span className="min-w-0">
                  <span className="text-fg-3">{r.area}: </span>
                  {r.rule}
                </span>
                <Badge tone="warning">
                  <ShieldAlert /> No platform control
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="flex items-center gap-2 text-[13px] text-fg-2">
            <ShieldCheck className="size-4 text-good-text" aria-hidden /> The agent platform reported controls for every extracted rule.
          </p>
        )}
      </ReviewStep>

      {external ? (
        <ReviewStep {...step("principals")} summary="Principal resolution: how each session is tied to the person the agent acted for — required for end users to claim under your third-party cover.">
          <EndUsersSection principals={profile.principals} />
        </ReviewStep>
      ) : (
        <ReviewStep {...step("principals")} summary="This agent works for your own people and systems. Sessions are attributed to the internal user or calling system; losses it causes are first-party (your own).">
          <InternalPrincipalsSection principals={profile.principals} audience={profile.audience} />
        </ReviewStep>
      )}

      <ReviewStep {...step("packs")} summary={`${enabledPacks.length} of ${Math.max(profile.packs.length, registry?.length ?? 0)} packs enabled automatically from the agent's capabilities`}>
        <PacksGrid activations={profile.packs} registry={registry} />
      </ReviewStep>

      <ReviewStep
        {...step("suggestions")}
        summary={
          profile.suggestions.length
            ? `${profile.suggestions.length} control${profile.suggestions.length === 1 ? "" : "s"} for the platform that runs this agent's tools to enforce`
            : "No suggestions right now"
        }
      >
        {profile.suggestions.length ? (
          <div className="flex flex-col gap-3">
            {profile.suggestions.map((s) => (
              <SuggestionCard key={s.id} suggestion={s} />
            ))}
          </div>
        ) : (
          <EmptyState className="py-6" title="Nothing to suggest" description="The agent platform already reports controls for this agent's authority profile." />
        )}
      </ReviewStep>

      <ReviewStep {...step("baseline")} summary="Detector thresholds come from this agent's own behavior once the baseline is credible.">
        <BaselineSection baseline={profile.baseline} />
      </ReviewStep>

      <ReviewStep {...step("projection")} summary="What the cover looks like once this review is complete.">
        <ProjectionSection profile={profile} pendingTools={pendingAll.length} toAdopt={toAdopt.length} uncontrolled={uncontrolled.length} action={confirmButton("Confirm all tools")} />
      </ReviewStep>
    </div>
  );
}

function StatusBanner({ profile, pendingHigh, pendingAll, action }: { profile: RiskProfile; pendingHigh: number; pendingAll: number; action: ReactNode }) {
  if (profile.status === "learning") {
    const { sessions, target } = profile.baseline;
    return (
      <Callout
        tone="info"
        icon={<Hourglass />}
        title={
          <span className="tabular">
            Collecting baseline · {fmtInt(sessions)} / {fmtInt(target)} sessions
          </span>
        }
        actions={action}
      >
        <span className="block">Until the baseline is credible, detectors use conservative global defaults and the score and projected limit are provisional.</span>
        <Meter value={sessions} max={target} className="mt-2 max-w-md" label="Baseline sessions collected" />
      </Callout>
    );
  }
  if (profile.status === "ready_for_review" || pendingAll > 0) {
    const n = pendingHigh || pendingAll;
    return (
      <Callout
        tone="warning"
        icon={<ShieldAlert />}
        title={`${n} ${pendingHigh ? "high-risk " : ""}tool${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} confirmation — unconfirmed scenarios are not covered`}
        actions={
          <>
            <a href="#actions" className={buttonVariants({ variant: "outline", size: "sm" })}>
              Review tools
            </a>
            {action}
          </>
        }
      >
        Your confirmation is the attestation the carrier relies on. Until then the related scenarios stay <span className="font-medium text-fg">unobserved</span>.
      </Callout>
    );
  }
  return (
    <Callout tone="good" icon={<ShieldCheck />} title="Risk profile confirmed">
      Every high-risk tool is attested. Re-profiling continues automatically — new tools, prompt or model changes will show up here for review.
    </Callout>
  );
}

function EndUsersSection({ principals }: { principals: RiskProfile["principals"] }) {
  const ok = principals.resolvedShare >= PRINCIPAL_TARGET;
  const unresolved = 1 - principals.resolvedShare;
  return (
    <div className="grid gap-5 md:grid-cols-3">
      <Field label="Resolution method">
        <span className="font-mono text-[12px] text-fg-2">{principals.method}</span>
      </Field>
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Sessions resolved to an end user</span>
        <div className="flex items-center gap-3">
          <Meter value={principals.resolvedShare} label="Share of sessions resolved to an end user" className="flex-1" tone={ok ? "var(--accent)" : "var(--warning)"} />
          <span className="text-[13px] font-medium tabular">{fmtPct(principals.resolvedShare, 1)}</span>
        </div>
        <span className="text-[11px] text-fg-3">
          {ok ? <Users className="mr-1 inline size-3 align-[-2px]" aria-hidden /> : <ShieldAlert className="mr-1 inline size-3 align-[-2px] text-warning-text" aria-hidden />}
          {fmtPct(unresolved, 1)} of sessions can't be tied to an end user — third-party losses there aren't claimable.
        </span>
      </div>
      <Field label="B2B buyer mapping">
        <span className="font-mono text-[12px] text-fg-2">{principals.b2bMapping}</span>
      </Field>
    </div>
  );
}

function PacksGrid({ activations, registry }: { activations: PackActivation[]; registry: PackInfo[] | undefined }) {
  // Registry order (core first), merged with this agent's activation state. Packs the registry
  // doesn't list (e.g. still loading) fall back to the activation record.
  const byId = new Map(activations.map((a) => [a.id, a]));
  const ids = [...new Set([...(registry ?? []).map((p) => p.id), ...activations.map((a) => a.id)])];
  const rows = ids
    .map((id) => {
      const info = registry?.find((p) => p.id === id);
      const act = byId.get(id);
      return {
        id,
        name: info?.name ?? act?.name ?? id,
        description: info?.description ?? act?.description ?? "",
        activatesWhen: info?.activatesWhen,
        core: info?.core ?? act?.core ?? false,
        enabled: act?.enabled ?? Boolean(info?.core),
        reason: act?.reason ?? (info?.core ? "Always on" : "Capability not observed for this agent"),
        detectors: act?.detectors ?? info?.signalTypes.length ?? 0,
      };
    })
    .sort((a, b) => Number(b.core) - Number(a.core) || Number(b.enabled) - Number(a.enabled));
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {rows.map((p) => (
        <div key={p.id} className={cn("flex flex-col gap-2 rounded-lg border p-3", p.enabled ? "bg-surface" : "border-dashed bg-transparent")}>
          <div className="flex items-start justify-between gap-2">
            <h3 className={cn("text-[13px] font-medium", !p.enabled && "text-fg-2")}>
              {p.name}
              {p.core ? <span className="ml-1.5 text-[11px] font-normal text-fg-3">core</span> : null}
            </h3>
            {p.enabled ? (
              <Badge tone="good">
                <CheckCircle2 /> On
              </Badge>
            ) : (
              <Badge tone="neutral">Off</Badge>
            )}
          </div>
          {p.description ? <p className="text-xs text-fg-2">{p.description}</p> : null}
          {p.activatesWhen ? (
            <p className="text-[11px] text-fg-3">
              <span className="font-medium text-fg-2">Activates when:</span> {p.activatesWhen}
            </p>
          ) : null}
          <p className="flex-1 text-[11px] text-fg-3">
            <span className="font-medium text-fg-2">{p.enabled ? "Why on:" : "Why off:"}</span> {p.reason}
          </p>
          <p className="text-[11px] text-fg-3 tabular">{p.detectors ? `${p.detectors} detectors` : "No detectors yet"}</p>
        </div>
      ))}
    </div>
  );
}

function InternalPrincipalsSection({ principals, audience }: { principals: RiskProfile["principals"]; audience: RiskProfile["audience"] }) {
  return (
    <div className="grid gap-5 md:grid-cols-3">
      <Field label="Acts for">{AUDIENCE_LABEL[audience]}</Field>
      <Field label="Attribution method">
        <span className="font-mono text-[12px] text-fg-2">{principals.method}</span>
      </Field>
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Sessions attributed</span>
        <div className="flex items-center gap-3">
          <Meter value={principals.resolvedShare} label="Share of sessions attributed to a user or system" className="flex-1" />
          <span className="text-[13px] font-medium tabular">{fmtPct(principals.resolvedShare, 1)}</span>
        </div>
        <span className="text-[11px] text-fg-3">No end-user claims apply — cover for this agent is first-party.</span>
      </div>
    </div>
  );
}

function BaselineSection({ baseline }: { baseline: RiskProfile["baseline"] }) {
  const credible = baseline.status === "credible";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {credible ? (
            <Badge tone="good">
              <CheckCircle2 /> Credible
            </Badge>
          ) : (
            <Badge tone="info">
              <Hourglass /> Learning
            </Badge>
          )}
          <span className="text-[13px] text-fg-2">{credible && baseline.credibleSince ? `since ${fmtDate(baseline.credibleSince)}` : `${fmtInt(Math.max(0, baseline.target - baseline.sessions))} sessions to go`}</span>
        </div>
        <span className="text-[13px] tabular text-fg-2">
          <span className="font-medium text-fg">{fmtInt(baseline.sessions)}</span> / {fmtInt(baseline.target)} sessions
        </span>
      </div>
      <Meter value={Math.min(baseline.sessions, baseline.target)} max={baseline.target} label="Baseline sessions" />
      <p className="text-xs text-fg-3">Sessions with S1/S2 signals are excluded from the baseline, so the agent can't learn that bad behavior is normal.</p>
    </div>
  );
}

function ProjectionSection({ profile, pendingTools, toAdopt, uncontrolled, action }: { profile: RiskProfile; pendingTools: number; toAdopt: number; uncontrolled: number; action: ReactNode }) {
  const p = profile.projection;
  const delta = p.projectedLimit.amount - p.currentLimit.amount;
  const pct = p.currentLimit.amount ? delta / p.currentLimit.amount : 0;
  const items = [
    { done: pendingTools === 0, label: pendingTools ? `Confirm ${pendingTools} tool classification${pendingTools === 1 ? "" : "s"}` : "All tool classifications confirmed" },
    { done: toAdopt === 0, label: toAdopt ? `Set up ${toAdopt} suggested control${toAdopt === 1 ? "" : "s"} on your agent platform` : "Suggested controls in place" },
    { done: uncontrolled === 0, label: uncontrolled ? `Get platform controls for ${uncontrolled} authority rule${uncontrolled === 1 ? "" : "s"}` : "Authority profile fully controlled" },
  ];
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex flex-wrap items-center gap-4 sm:gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Current limit</span>
          <span className="text-2xl font-semibold tracking-tight tabular">{fmtMoney(p.currentLimit)}</span>
          <TierBadge tier={p.currentTier} className="self-start" />
        </div>
        <ArrowRight className="size-5 text-fg-3" aria-label="projected to" />
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Projected limit</span>
          <span className="text-2xl font-semibold tracking-tight tabular text-accent">{fmtMoney(p.projectedLimit)}</span>
          <TierBadge tier={p.projectedTier} className="self-start" />
        </div>
        <div className="flex flex-col gap-1 sm:ml-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Change</span>
          <span className="text-[15px] font-medium tabular">
            {delta >= 0 ? "+" : "−"}
            {fmtMoney(Math.abs(delta))}
          </span>
          <span className="text-xs text-fg-3 tabular">{delta >= 0 ? "+" : "−"}{fmtPct(Math.abs(pct))}</span>
        </div>
      </div>
      <div className="flex flex-col gap-3 rounded-lg border bg-surface-2/40 p-3">
        <p className="text-[13px] font-medium">Confirm all + set up suggested controls</p>
        <ul className="flex flex-col gap-1.5">
          {items.map((i) => (
            <li key={i.label} className="flex items-center gap-2 text-[13px]">
              {i.done ? <CheckCircle2 className="size-4 shrink-0 text-good-text" aria-label="Done" /> : <span className="size-4 shrink-0 rounded-full border-2 border-border-strong" aria-label="To do" />}
              <span className={cn(i.done ? "text-fg-3" : "text-fg")}>{i.label}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          {action}
        </div>
        <p className="text-[11px] text-fg-3">Projection assumes current traffic and issue states; the limit changes by endorsement once verified.</p>
      </div>
    </div>
  );
}
